/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ApiConfig} from '../../config/api-config';
import {interpolateTemplate} from '../../common/filters/interpolate-template';
import {CreativePreviewApiService} from '../../creative/creative-preview-api.service';

export class FullScreenPreviewContainment {
  private readonly container: HTMLElement | null;
  private observer: MutationObserver;

  constructor(private readonly window: any, private readonly target: HTMLElement) {
    this.container = target.parentElement;
    this.update();
    if (!window.MutationObserver) {
      return;
    }
    this.observer = new window.MutationObserver(() => this.update());
    this.observer.observe(target, {
      attributeFilter: ['data-aim', 'style'],
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.target.classList.remove('creativePreviewTargetFullScreen');
    this.container?.classList.remove('creativePreviewPageFullScreen');
  }

  private update(): void {
    const fullScreen = _.some(this.target.querySelectorAll('iframe'), (iframe: HTMLIFrameElement) => {
      const viewportHeight = iframe.style.height === '100vh' || iframe.style.height === '200vh';
      const responsiveWidth = iframe.style.width === '100%' || iframe.style.width === '100vw';
      return iframe.getAttribute('data-aim') === 'impact'
        || (viewportHeight && (responsiveWidth || iframe.style.position === 'fixed'));
    });
    this.target.classList.toggle('creativePreviewTargetFullScreen', fullScreen);
    this.container?.classList.toggle('creativePreviewPageFullScreen', fullScreen);
  }
}

export function doPreview(
  window: any,
  document: any,
  location: any,
  $element: any,
  refreshView: (() => void) | null,
  previewData: any,
  isPublic: boolean,
  creativePreviewApi: CreativePreviewApiService,
  apiConfig: ApiConfig,
  ctrl: any,
  targetId?: string
): void {
  void document;
  void location;
  void $element;

  function checkForImpCallback() {
    if (_.isFunction(previewData.impressionCallback)) {
      previewData.impressionCallback();
    }
  }

  function refreshAngularView() {
    refreshView?.();
  }

  function buildDirectRequest(env: string, creativeContent: string): any {
    const directRequest: any = {};
    _.assign(directRequest, _.pick(creativeData, [
      'targetId',
      'isolateFrame',
      'blockScript',
      'clearTarget',
      'onImpressionResponse',
      'creativeWidth',
      'creativeHeight',
      'lineItemCode'
    ]));
    directRequest.creativeContent = creativeContent;
    directRequest.env = env;
    directRequest.networkId = previewData.networkId;
    if (previewData.dataLang) {
      directRequest.attrs = {lang: previewData.dataLang};
    }
    return directRequest;
  }

  const safeTargetId = targetId || 'targetId';
  const scriptIsOn = _.isUndefined(previewData.allowScript) ? true : previewData.allowScript;
  const creativeData: any = {
    creativeId: previewData.creativeId,
    lineItemId: previewData.lineItemId,
    lineItemCode: previewData.lineItemCode,
    targetId: safeTargetId,
    creativeData: previewData,
    creativeWidth: previewData.creativeWidth,
    creativeHeight: previewData.creativeHeight,
    isolateFrame: !scriptIsOn,
    blockScript: !scriptIsOn,
    clearTarget: previewData.clearTarget,
    onImpressionResponse: function() {
      checkForImpCallback();
    }
  };

  setTimeout(function() {
    window.adn = window.adn || {};
    window.adn.calls = window.adn.calls || [];
    const env = apiConfig.getId().replace('_adn', '');
    window.adn.calls.push(function() {
      // clearDivs wipes every ad on the page. Skip it when the request already
      // clears its own target (multi-preview lists / show-more batches).
      if (!creativeData.clearTarget && _.isFunction(window.adn.clearDivs)) {
        window.adn.clearDivs();
      }

      // Layout previews (e.g. VAST) stash creativeContent in local storage for blank-page preview.
      if (previewData.creativeContent) {
        window.adn.request(buildDirectRequest(env, previewData.creativeContent));
        return;
      }

      const request: any = {
        env: env,
        networkId: previewData.networkId,
        creatives: [creativeData],
        onError: function() {
          function extractCreativePreviewError(errData?: any): {context: any; message: string; args: any} | null {
            const body = _.get(errData, 'data') !== undefined ? _.get(errData, 'data') : errData;
            if (!body) {
              return null;
            }

            // /creativepreview wraps Message/ValidationResult in {html: ...} for JSON errors.
            // ValidationResult serialises as errors[].error = {code, text, parameters}.
            const payload = _.isObject(body.html) && !_.isArray(body.html) ? body.html : body;
            const firstError = _.get(payload, ['errors', 0]);
            const errorInfo = _.get(firstError, 'error')
              || (_.isObject(_.get(payload, 'error')) ? payload.error : null)
              || (_.get(payload, 'text') ? payload : null);

            const rawText = _.get(errorInfo, 'text')
              || (_.isString(_.get(firstError, 'error')) ? firstError.error : null)
              || _.get(payload, 'text')
              || (_.isString(body.html) ? body.html : null);

            if (!rawText) {
              return null;
            }

            const parameters = _.get(errorInfo, 'parameters') || {};
            const message = _.isString(rawText) ? interpolateTemplate(rawText)(parameters) : String(rawText);

            return {
              context: _.get(firstError, 'context')
                || _.get(errorInfo, 'code')
                || _.get(payload, 'code')
                || 'creativepreview',
              message,
              args: parameters
            };
          }

          function reportPreviewFailure(errData?: any) {
            const extracted = extractCreativePreviewError(errData);
            if (extracted) {
              if (isPublic) {
                ctrl.publicError = extracted.message;
                ctrl.errorMessage = null;
              } else {
                ctrl.publicError = null;
                ctrl.errorMessage = extracted;
              }
            } else if (isPublic) {
              ctrl.publicError = 'We did not succeed in providing a preview.';
              ctrl.errorMessage = null;
            } else {
              ctrl.publicError = null;
              ctrl.errorMessage = {
                context: 'creativepreview',
                message: 'Preview is not possible for this creative.',
                args: ''
              };
            }
            refreshAngularView();
            checkForImpCallback();
          }

          if (!previewData.creativeId || !creativePreviewApi) {
            reportPreviewFailure();
            return;
          }

          creativePreviewApi.get(previewData.creativeId).subscribe({
            next: (data) => {
              if (!data || !_.isString(data.html) || !data.html) {
                reportPreviewFailure(data);
                return;
              }

              creativeData.creativeContent = data.html;
              window.adn.request(buildDirectRequest(env, data.html));
            },
            error: (errData) => reportPreviewFailure(errData)
          });
        }
      };
      if (previewData.dataLang) {
        request.attrs = {lang: previewData.dataLang};
      }
      window.adn.request(request);
    });
  }, 200);
}
